import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getChallengesAdmin, getAnalyticsData } from '../services/WebCloudFunctionsService';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Modern analytics interfaces
interface ChallengeAnalytics {
    challenge: {
        id: string;
        title: string;
        emoji: string;
        targetValue: number;
        startDate: string;
        endDate: string;
        isActive: boolean;
    };
    metrics: {
        totalParticipants: number;
        completedParticipants: number;
        inProgressParticipants: number;
        completionRate: number; // Daily challenge completion rate - how often participants meet daily goals
        averageProgress: number; // Overall progress in challenge journey - where participants are in their journey
        totalPointsEarned: number;
        daysLeft: number;
        daysElapsed: number;
        totalDays: number;
        timelineProgress: number;
    };
    progressDistribution: { [key: string]: number };
    topPerformers: Array<{
        userId: string;
        displayName: string;
        currentValue: number;
        targetValue: number;
        progress: number;
        pointsEarned: number;
        lastActivity: string;
    }>;
}

interface SystemMetrics {
    totalChallenges: number;
    activeChallenges: number;
    completedChallenges: number;
    totalParticipants: number;
    totalPointsEarned: number;
}

interface DailyTrend {
    date: string;
    completions: number;
    participants: number;
    averageProgress: number;
}

interface AnalyticsData {
    challengeAnalytics: ChallengeAnalytics | null;
    allChallenges: { [challengeId: string]: ChallengeAnalytics };
    systemMetrics: SystemMetrics;
    averageEngagement: number;
    systemTopPerformers: Array<{
        userId: string;
        displayName: string;
        averageProgress: number;
        participationsCount: number;
    }>;
    dailyTrends: DailyTrend[];
    filters: { challengeId?: string; organizationId?: string; timeRange: string; level?: string };
    generatedAt: string;
}

const Analytics: React.FC = () => {
    const { user } = useAuth();
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [challenges, setChallenges] = useState<any[]>([]);
    const [selectedChallenge, setSelectedChallenge] = useState<string>('all');
    const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '180d'>('30d');
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeView, setActiveView] = useState<'overview' | 'leaderboard' | 'dailyCompletions'>('overview');

    // Memoized filters
    const currentFilters = useMemo(() => ({
        challengeId: selectedChallenge === 'all' ? undefined : selectedChallenge,
        timeRange: timeRange,
        useCache: true
    }), [selectedChallenge, timeRange]);

    const loadChallenges = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const challengesData = await getChallengesAdmin();
            if (challengesData && challengesData.challenges && Array.isArray(challengesData.challenges)) {
                setChallenges(challengesData.challenges);
            } else {
                setChallenges([]);
            }
        } catch (error) {
            console.error('Failed to load challenges:', error);
            setError('Failed to load challenges. Please try again.');
            setChallenges([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Load analytics data using the fixed getAnalyticsData function
    const loadAnalytics = useCallback(async (forceRefresh = false) => {
        try {
            setError(null);
            if (forceRefresh) {
                setIsRefreshing(true);
            } else {
                setIsLoading(true);
            }

            console.log('📊 Loading analytics data using getAnalyticsData...');

            // Use the fixed getAnalyticsData function
            const analyticsData = await getAnalyticsData({
                challengeId: currentFilters.challengeId,
                // NOTE: Temporarily omit organizationId filter because current backend analytics
                // implementation is returning empty challengeAnalytics/dailyTrends when filtered
                // by organization, even though there are active challenges/participations.
                // This ensures we at least see system-wide analytics data.
                organizationId: undefined,
                timeRange: currentFilters.timeRange,
                level: undefined // Get all levels
            });

            console.log('📊 Analytics data loaded successfully:', analyticsData);
            console.log('📊 Analytics data type:', typeof analyticsData);
            console.log('📊 Analytics data keys:', analyticsData ? Object.keys(analyticsData) : 'null');

            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/dd95de8b-4aae-4806-ab49-a2b65a05ac50', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId: 'debug-session',
                    runId: 'run1',
                    hypothesisId: 'H3',
                    location: 'web/src/admin/Analytics.tsx:loadAnalytics:before-unwrap',
                    message: 'loadAnalytics received data',
                    data: {
                        filters: currentFilters,
                        hasData: !!analyticsData,
                        keys: analyticsData ? Object.keys(analyticsData as any) : [],
                    },
                    timestamp: Date.now(),
                }),
            }).catch(() => {});
            // #endregion agent log

            // The service function already unwraps result.data, so use the data directly
            // Handle case where data might be wrapped (for backward compatibility)
            let unwrappedData = analyticsData;
            
            // Check if it's wrapped in a data property
            if ((analyticsData as any)?.data && typeof (analyticsData as any).data === 'object') {
                unwrappedData = (analyticsData as any).data;
                console.log('📊 Unwrapped from .data property');
            }
            
            // Check if it's wrapped with success flag
            if ((unwrappedData as any)?.success && (unwrappedData as any)?.data) {
                unwrappedData = (unwrappedData as any).data;
                console.log('📊 Unwrapped from success.data structure');
            }

            console.log('📊 Final data structure:', {
                hasSystemMetrics: !!(unwrappedData as any)?.systemMetrics,
                hasChallengeAnalytics: !!(unwrappedData as any)?.challengeAnalytics,
                hasAllChallenges: !!(unwrappedData as any)?.allChallenges,
                allChallengesKeys: unwrappedData?.allChallenges ? Object.keys(unwrappedData.allChallenges) : [],
                systemMetrics: unwrappedData?.systemMetrics,
            });

            // Convert the analytics data to match the expected interface
            const convertedData: AnalyticsData = {
                challengeAnalytics: unwrappedData?.challengeAnalytics || null,
                allChallenges: unwrappedData?.allChallenges || {},
                systemMetrics: unwrappedData?.systemMetrics || {
                    totalChallenges: 0,
                    activeChallenges: 0,
                    completedChallenges: 0,
                    totalParticipants: 0,
                    totalPointsEarned: 0
                },
                averageEngagement: unwrappedData?.averageEngagement || 0,
                systemTopPerformers: unwrappedData?.systemTopPerformers || [],
                dailyTrends: unwrappedData?.dailyTrends || [],
                filters: unwrappedData?.filters || { timeRange: currentFilters.timeRange },
                generatedAt: unwrappedData?.generatedAt || new Date().toISOString()
            };
            
            console.log('📊 Converted data:', {
                hasSystemMetrics: !!convertedData.systemMetrics,
                allChallengesCount: convertedData.allChallenges ? Object.keys(convertedData.allChallenges).length : 0,
                dailyTrendsCount: convertedData.dailyTrends?.length || 0,
            });

            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/dd95de8b-4aae-4806-ab49-a2b65a05ac50', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId: 'debug-session',
                    runId: 'run1',
                    hypothesisId: 'H3',
                    location: 'web/src/admin/Analytics.tsx:loadAnalytics:after-convert',
                    message: 'loadAnalytics converted data',
                    data: {
                        hasSystemMetrics: !!convertedData.systemMetrics,
                        allChallengesCount: convertedData.allChallenges ? Object.keys(convertedData.allChallenges).length : 0,
                        dailyTrendsCount: convertedData.dailyTrends?.length || 0,
                        selectedChallenge,
                    },
                    timestamp: Date.now(),
                }),
            }).catch(() => {});
            // #endregion agent log

            setAnalyticsData(convertedData);
        } catch (error: any) {
            console.error('Failed to load analytics:', error);
            setError(error?.message || 'Failed to load analytics data. Please try again.');
            setAnalyticsData(null);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [currentFilters, user?.organizationId]);

    useEffect(() => {
        loadChallenges();
    }, []);

    useEffect(() => {
        if (challenges.length > 0) {
            loadAnalytics();
        }
    }, [currentFilters, challenges, loadAnalytics]);

    const handleRefresh = () => {
        loadAnalytics(true);
    };

    // Utility function to generate CSV for daily completions
    const generateDailyCompletionsCSV = () => {
        if (!analyticsData?.dailyTrends || analyticsData.dailyTrends.length === 0) {
            alert('No data available to export');
            return;
        }

        const csvData = analyticsData.dailyTrends
            .filter(day => day.participants > 0 || day.completions > 0)
            .map(day => ({
                Date: day.date,
                Participants: day.participants,
                Completions: day.completions,
                'Average Progress': `${day.averageProgress.toFixed(1)}%`,
                'Success Rate': `${day.participants > 0 ? ((day.completions / day.participants) * 100).toFixed(1) : 0}%`
            }));

        const csvContent = [
            Object.keys(csvData[0]).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `daily-completions-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (challenges.length > 0) {
            loadAnalytics();
        }
    }, [challenges, loadAnalytics]);

    const formatNumber = (num: number) => {
        if (num >= 1000000) return Math.round(num / 1000000) + 'M';
        if (num >= 1000) return Math.round(num / 1000) + 'K';
        return Math.round(num).toString();
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getProgressColor = (progress: number) => {
        if (progress >= 80) return 'text-green-500';
        if (progress >= 60) return 'text-yellow-500';
        return 'text-red-500';
    };

    const getProgressBarColor = (progress: number) => {
        if (progress >= 80) return 'bg-green-500';
        if (progress >= 60) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    // Helper functions for insights
    const getPeakActivityDay = (dailyTrends: DailyTrend[]) => {
        if (dailyTrends.length === 0) return 'No data';
        const maxParticipants = Math.max(...dailyTrends.map(d => d.participants));
        const peakDay = dailyTrends.find(d => d.participants === maxParticipants);
        return peakDay ? formatDate(peakDay.date) : 'No data';
    };

    const getBestProgressDay = (dailyTrends: DailyTrend[]) => {
        if (dailyTrends.length === 0) return 'No data';
        const maxProgress = Math.max(...dailyTrends.map(d => d.averageProgress));
        const bestDay = dailyTrends.find(d => d.averageProgress === maxProgress);
        return bestDay ? formatDate(bestDay.date) : 'No data';
    };

    const getTotalCompletions = (dailyTrends: DailyTrend[]) => {
        return dailyTrends.reduce((sum, day) => sum + day.completions, 0);
    };

    const getAverageDailyProgress = (dailyTrends: DailyTrend[]) => {
        if (dailyTrends.length === 0) return 0;
        return (dailyTrends.reduce((sum, day) => sum + day.averageProgress, 0) / dailyTrends.length);
    };

    const getPeakParticipantsCount = (dailyTrends: DailyTrend[]) => {
        if (dailyTrends.length === 0) return 0;
        return Math.round(Math.max(...dailyTrends.map(d => d.participants)));
    };

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center py-10">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="w-full h-full p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                        <div className="text-center">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                No Analytics Data Available
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                {error || 'Analytics data is being calculated. This may take a few moments for the first time.'}
                            </p>
                            <button
                                onClick={() => loadAnalytics(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            >
                                Calculate Analytics
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Validate that we have the expected data structure
    if (!analyticsData.systemMetrics) {
        console.error('❌ Invalid analytics data structure:', analyticsData);
        return (
            <div className="w-full h-full p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                        <div className="text-center">
                            <div className="text-red-400 text-6xl mb-4">⚠️</div>
                            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                Invalid Analytics Data Structure
                            </h3>
                            <p className="text-red-600 dark:text-red-300 mb-4">
                                The backend returned an unexpected data format. This might be due to a version mismatch.
                            </p>
                            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded text-left text-sm">
                                <p className="font-medium mb-2">Expected structure:</p>
                                <pre className="text-xs overflow-auto">
                                    {JSON.stringify({
                                        systemMetrics: { totalChallenges: 0, activeChallenges: 0, completedChallenges: 0, totalParticipants: 0, totalPointsEarned: 0 },
                                        challengeAnalytics: null,
                                        allChallenges: {},
                                        dailyTrends: [],
                                        systemTopPerformers: []
                                    }, null, 2)}
                                </pre>
                                <p className="font-medium mt-2 mb-2">Received structure:</p>
                                <pre className="text-xs overflow-auto">
                                    {JSON.stringify(analyticsData, null, 2)}
                                </pre>
                            </div>
                            <button
                                onClick={() => loadAnalytics(true)}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentChallengeData = selectedChallenge === 'all'
        ? null
        : analyticsData.challengeAnalytics || analyticsData.allChallenges?.[selectedChallenge];

    return (
        <div className="w-full h-full p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                                📊 Analytics Dashboard
                            </h1>
                            <p className="text-gray-600 dark:text-gray-300 text-base">
                                Real-time insights into challenge performance and user engagement
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="text-sm text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 px-3 py-2 rounded-lg backdrop-blur-sm">
                                Last updated: {new Date(analyticsData.generatedAt).toLocaleTimeString()}
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 focus:ring-4 focus:ring-blue-500/30 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                            >
                                {isRefreshing ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Refreshing...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>🔄</span>
                                        <span>Refresh</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Metrics Explanation */}
                {selectedChallenge === 'all' && (
                    <div className="bg-blue-50/80 dark:bg-blue-900/20 backdrop-blur-sm rounded-2xl border border-blue-200/50 dark:border-blue-700/30 p-6 mb-8">
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-600 dark:text-blue-400 text-xl">💡</div>
                            <div>
                                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">Understanding the Metrics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-blue-300">
                                    <div>
                                        <strong>Completion Rate:</strong> Percentage of days where participants completed daily challenges.
                                        This measures daily engagement consistency.
                                    </div>
                                    <div>
                                        <strong>Average Progress:</strong> Where participants are in their overall challenge journey.
                                        This shows the collective progress toward the final goal.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* System Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                        <div className="flex items-center">
                            <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {selectedChallenge === 'all' ? 'Total Challenges' : 'Challenge Participants'}
                                </p>
                                <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                                    {selectedChallenge === 'all'
                                        ? formatNumber(analyticsData.systemMetrics.totalChallenges)
                                        : formatNumber(currentChallengeData?.metrics.totalParticipants || 0)
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                        <div className="flex items-center">
                            <div className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg">
                                <span className="text-2xl">🚀</span>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {selectedChallenge === 'all' ? 'Active Challenges' : 'Completion Rate'}
                                </p>
                                <p className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">
                                    {selectedChallenge === 'all'
                                        ? formatNumber(analyticsData.systemMetrics.activeChallenges)
                                        : `${currentChallengeData?.metrics.completionRate.toFixed(1) || 0}%`
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                        <div className="flex items-center">
                            <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl shadow-lg">
                                <span className="text-2xl">👥</span>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {selectedChallenge === 'all' ? 'Total Participants' : 'Avg Progress'}
                                </p>
                                <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent">
                                    {selectedChallenge === 'all'
                                        ? formatNumber(analyticsData.systemMetrics.totalParticipants)
                                        : `${currentChallengeData?.metrics.averageProgress.toFixed(1) || 0}%`
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                        <div className="flex items-center">
                            <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl shadow-lg">
                                <span className="text-2xl">⭐</span>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {selectedChallenge === 'all' ? 'Total Points' : 'Days Left'}
                                </p>
                                <p className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent">
                                    {selectedChallenge === 'all'
                                        ? formatNumber(analyticsData.systemMetrics.totalPointsEarned)
                                        : currentChallengeData?.metrics.daysLeft || 0
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Filters and Tabs */}
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 mb-6">
                    <div className="flex flex-wrap gap-6 items-center mb-6">
                        <div className="flex-1 min-w-64">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                🎯 Select Challenge
                            </label>
                            <select
                                value={selectedChallenge}
                                onChange={(e) => setSelectedChallenge(e.target.value)}
                                className="w-full px-4 py-3 border-0 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 text-gray-900 dark:text-white focus:ring-4 focus:ring-blue-500/30 focus:outline-none shadow-inner transition-all duration-300"
                            >
                                <option value="all">All Challenges Overview</option>
                                {challenges.map((challenge) => (
                                    <option key={challenge.id} value={challenge.id}>
                                        {challenge.emoji} {challenge.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                ⏰ Time Range
                            </label>
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d' | '180d')}
                                className="px-4 py-3 border-0 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-700 dark:to-gray-800 text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/30 focus:outline-none shadow-inner transition-all duration-300"
                            >
                                <option value="7d">Last 7 Days</option>
                                <option value="30d">Last 30 Days</option>
                                <option value="90d">Last 90 Days</option>
                                <option value="180d">Last 180 Days</option>
                            </select>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        <button
                            onClick={() => setActiveView('overview')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeView === 'overview'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                        >
                            📊 Overview
                        </button>
                        <button
                            onClick={() => setActiveView('leaderboard')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeView === 'leaderboard'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                        >
                            🏆 Leaderboard
                        </button>
                        <button
                            onClick={() => setActiveView('dailyCompletions')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeView === 'dailyCompletions'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                        >
                            📅 Daily Completions
                        </button>
                    </div>
                </div>
                {/* Chart Insights */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 dark:from-blue-400/20 dark:to-blue-500/30 rounded-xl p-4 border border-blue-200/50 dark:border-blue-700/30">
                        <div className="flex items-center space-x-2 mb-3">
                            <div className="text-blue-600 dark:text-blue-400 text-xl">📈</div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Peak Activity</div>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">{getPeakActivityDay(analyticsData.dailyTrends)}</div>
                        <div className="text-xs text-blue-600 dark:text-blue-400">{getPeakParticipantsCount(analyticsData.dailyTrends)} participants</div>
                    </div>

                    <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/20 dark:from-emerald-400/20 dark:to-emerald-500/30 rounded-xl p-4 border border-emerald-200/50 dark:border-emerald-700/30">
                        <div className="flex items-center space-x-2 mb-3">
                            <div className="text-emerald-600 dark:text-emerald-400 text-xl">🏆</div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Best Progress</div>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">{getBestProgressDay(analyticsData.dailyTrends)}</div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">Highest achievement</div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/20 dark:from-purple-400/20 dark:to-purple-500/30 rounded-xl p-4 border border-purple-200/50 dark:border-purple-700/30">
                        <div className="flex items-center space-x-2 mb-3">
                            <div className="text-purple-600 dark:text-purple-400 text-xl">✅</div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Total Completions</div>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">{getTotalCompletions(analyticsData.dailyTrends)}</div>
                        <div className="text-xs text-purple-600 dark:text-purple-400">Daily achievements</div>
                    </div>

                    <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/20 dark:from-amber-400/20 dark:to-amber-500/30 rounded-xl p-4 border border-amber-200/50 dark:border-amber-700/30">
                        <div className="flex items-center space-x-2 mb-3">
                            <div className="text-amber-600 dark:text-amber-400 text-xl">📊</div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Avg Progress</div>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">{getAverageDailyProgress(analyticsData.dailyTrends).toFixed(1)}%</div>
                        <div className="text-xs text-amber-600 dark:text-amber-400">Daily average</div>
                    </div>
                </div>

                {/* Challenge-Specific Analytics */}
                {currentChallengeData && (
                    <div className="mb-8">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                                🎯 {currentChallengeData.challenge.emoji} {currentChallengeData.challenge.title} Analytics
                            </h2>

                            {/* Metrics Explanation for Individual Challenge */}
                            <div className="bg-blue-50/80 dark:bg-blue-900/20 backdrop-blur-sm rounded-xl border border-blue-200/50 dark:border-blue-700/30 p-4 mb-6">
                                <div className="flex items-start space-x-3">
                                    <div className="text-blue-600 dark:text-blue-400 text-lg">💡</div>
                                    <div className="text-sm text-blue-700 dark:text-blue-300">
                                        <strong>Completion Rate:</strong> Daily engagement consistency • <strong>Avg Progress:</strong> Overall journey progress
                                    </div>
                                </div>
                            </div>

                            {/* Challenge Metrics Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Participants</p>
                                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {Math.round(currentChallengeData.metrics.totalParticipants)}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {Math.round(currentChallengeData.metrics.completedParticipants)} completed
                                    </p>
                                </div>

                                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg relative group">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center justify-center">
                                        Completion Rate
                                        <span className="ml-1 text-xs text-gray-400 cursor-help">ⓘ</span>
                                    </p>
                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        {currentChallengeData.metrics.completionRate.toFixed(1)}%
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Daily engagement
                                    </p>

                                    {/* Tooltip */}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                        Percentage of days where participants completed daily challenges
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>

                                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg relative group">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center justify-center">
                                        Avg Progress
                                        <span className="ml-1 text-xs text-gray-400 cursor-help">ⓘ</span>
                                    </p>
                                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                        {currentChallengeData.metrics.averageProgress.toFixed(1)}%
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Journey progress
                                    </p>

                                    {/* Tooltip */}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                        Where participants are in their overall challenge journey
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>

                                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Days Left</p>
                                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                        {currentChallengeData.metrics.daysLeft}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {currentChallengeData.metrics.timelineProgress.toFixed(1)}% of time elapsed
                                    </p>
                                </div>
                            </div>

                            {/* Progress Distribution */}
                            <div className="mb-6">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                                    📊 Progress Distribution
                                </h3>
                                <div className="flex items-center space-x-4">
                                    {Object.entries({ '0-25%': '🔴', '26-50%': '🟠', '51-75%': '🟡', '76-99%': '🟢', '100%': '✅' }).map(([range, emoji]) => {
                                        const count = currentChallengeData.progressDistribution[range] || 0;
                                        const totalUsers = currentChallengeData.metrics.totalParticipants || 1;
                                        const percentage = totalUsers > 0 ? (count / totalUsers) * 100 : 0;

                                        return (
                                            <div key={range} className="flex-1 text-center">
                                                <div className="flex items-center justify-center space-x-1 mb-2">
                                                    <span className="text-sm">{emoji}</span>
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{range}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-1">
                                                    <div
                                                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>
                                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                                    {count} ({percentage.toFixed(0)}%)
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>


                        </div>
                    </div>
                )}

                {/* All Challenges Overview */}
                {selectedChallenge === 'all' && analyticsData.allChallenges && Object.keys(analyticsData.allChallenges).length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                            📋 All Challenges Overview
                        </h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {Object.entries(analyticsData.allChallenges).map(([challengeId, challengeData]: [string, any]) => {
                                // Ensure challenge data has required structure
                                if (!challengeData || !challengeData.challenge) {
                                    console.warn('⚠️ Invalid challenge data structure:', challengeId, challengeData);
                                    return null;
                                }
                                return (
                                <div key={challengeId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {challengeData.challenge?.emoji || '🎯'} {challengeData.challenge?.title || 'Untitled Challenge'}
                                        </h3>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${challengeData.challenge?.isActive !== false
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                            }`}>
                                            {challengeData.challenge?.isActive !== false ? 'Active' : 'Completed'}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Participants</span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {challengeData.metrics?.totalParticipants || 0}
                                            </span>
                                        </div>

                                        <div className="flex justify-between items-center group relative">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                                                Completion Rate
                                                <span className="ml-1 text-xs text-gray-400 cursor-help">ⓘ</span>
                                            </span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {(challengeData.metrics?.completionRate || 0).toFixed(1)}%
                                            </span>

                                            {/* Tooltip */}
                                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                Daily engagement consistency
                                                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center group relative">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                                                Avg Progress
                                                <span className="ml-1 text-xs text-gray-400 cursor-help">ⓘ</span>
                                            </span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {(challengeData.metrics?.averageProgress || 0).toFixed(1)}%
                                            </span>

                                            {/* Tooltip */}
                                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                Journey progress
                                                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                            </div>
                                        </div>

                                        {challengeData.challenge.isActive && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Days Left</span>
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {challengeData.metrics?.daysLeft || 0}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                            })}
                        </div>
                    </div>
                )}

                {/* Beautiful Daily Trends Chart */}
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 mb-8">
                    <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
                        📈 Daily Trends ({timeRange})
                        {selectedChallenge !== 'all' && currentChallengeData && (
                            <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                                - {currentChallengeData.challenge.emoji} {currentChallengeData.challenge.title}
                            </span>
                        )}
                    </h2>

                    {analyticsData.dailyTrends.length > 0 ? (
                        <div className="h-96">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={analyticsData.dailyTrends.map(day => ({
                                        ...day,
                                        participants: Math.round(day.participants),
                                        completions: Math.round(day.completions),
                                        formattedDate: formatDate(day.date),
                                        fullDate: day.date
                                    }))}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                                    <XAxis
                                        dataKey="formattedDate"
                                        stroke="#6b7280"
                                        fontSize={12}
                                        tick={{ fill: '#6b7280' }}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        stroke="#6b7280"
                                        fontSize={12}
                                        tick={{ fill: '#6b7280' }}
                                        tickFormatter={(value) => Math.round(value).toString()}
                                        domain={[0, 'dataMax + 2']}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        stroke="#6b7280"
                                        fontSize={12}
                                        tick={{ fill: '#6b7280' }}
                                        domain={[0, 100]}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#1f2937',
                                            border: '1px solid #374151',
                                            borderRadius: '8px',
                                            color: '#f9fafb'
                                        }}
                                        labelStyle={{ color: '#f9fafb' }}
                                        formatter={(value: any, name: string) => {
                                            if (name === 'Average Progress (%)') {
                                                return [`${value.toFixed(1)}%`, 'Average Progress'];
                                            }
                                            if (name === 'Participants') {
                                                return [Math.round(value), 'Participants'];
                                            }
                                            if (name === 'Completions') {
                                                return [Math.round(value), 'Completions'];
                                            }
                                            return [Math.round(value), name];
                                        }}
                                        labelFormatter={(label) => `Date: ${label}`}
                                    />
                                    <Legend
                                        wrapperStyle={{
                                            paddingTop: '20px',
                                            color: '#6b7280'
                                        }}
                                    />



                                    {/* Line for participants */}
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="participants"
                                        stroke="#1d4ed8"
                                        strokeWidth={3}
                                        dot={{ fill: '#1d4ed8', strokeWidth: 2, r: 5 }}
                                        activeDot={{ r: 7, stroke: '#1d4ed8', strokeWidth: 3 }}
                                        name="Participants"
                                    />

                                    {/* Line for completions */}
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="completions"
                                        stroke="#10b981"
                                        strokeWidth={3}
                                        dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                                        activeDot={{ r: 7, stroke: '#10b981', strokeWidth: 3 }}
                                        name="Completions"
                                    />

                                    {/* Line for average progress */}
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="averageProgress"
                                        stroke="#8b5cf6"
                                        strokeWidth={3}
                                        dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                                        activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2 }}
                                        name="Average Progress (%)"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <div className="text-6xl mb-4">📈</div>
                            <p className="text-lg font-medium mb-2">No Trend Data Available</p>
                            <p>Data will appear once users start participating in challenges</p>
                        </div>
                    )}

                    {/* Chart Legend */}
                    {analyticsData.dailyTrends.length > 0 && (
                        <>
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <div className="w-4 h-4 bg-blue-700 rounded-full"></div>
                                    <div>
                                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Participants</p>
                                        <p className="text-xs text-blue-600 dark:text-blue-300">Total active users per day (rounded)</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                                    <div>
                                        <p className="text-sm font-medium text-green-800 dark:text-green-200">Completions</p>
                                        <p className="text-xs text-green-600 dark:text-green-300">Challenge completions per day</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                                    <div>
                                        <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Average Progress</p>
                                        <p className="text-xs text-purple-600 dark:text-purple-300">Daily progress percentage</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* System Top Performers */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                        🌟 {selectedChallenge === 'all' ? 'System Top Performers' : 'Challenge Top Performers'}
                        {selectedChallenge !== 'all' && currentChallengeData && (
                            <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                                - {currentChallengeData.challenge.emoji} {currentChallengeData.challenge.title}
                            </span>
                        )}
                    </h2>
                    <div className="space-y-3">
                        {selectedChallenge === 'all' ? (
                            // System-wide top performers
                            analyticsData.systemTopPerformers.length > 0 ? (
                                analyticsData.systemTopPerformers.map((performer, index) => (
                                    <div key={performer.userId} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{performer.displayName}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {performer.participationsCount} challenges
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-semibold ${getProgressColor(performer.averageProgress)}`}>
                                                {performer.averageProgress.toFixed(1)}%
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Average Progress
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <p>No system performance data available yet</p>
                                </div>
                            )
                        ) : (
                            // Challenge-specific top performers
                            currentChallengeData && currentChallengeData.topPerformers.length > 0 ? (
                                currentChallengeData.topPerformers.map((performer, index) => (
                                    <div key={performer.userId} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{performer.displayName}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {performer.currentValue} / {performer.targetValue} ({performer.progress.toFixed(1)}%)
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-semibold ${getProgressColor(performer.progress)}`}>
                                                {performer.progress.toFixed(1)}%
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {performer.pointsEarned} points
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <p>No challenge performance data available yet</p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;